// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Waft — pure zone geometry. Zones are numbered row-major: zone z sits at
// col = z % cols, row = floor(z / cols). Both the renderer (draw a firefly in a zone)
// and the tap fallback (which zone did the finger land in) derive their rectangles from
// HERE, so they can never disagree. Pure and unit-tested.

export interface Grid {
  cols: number;
  rows: number;
}

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export function zoneCount(g: Grid): number {
  return g.cols * g.rows;
}

/**
 * The outer padding and inter-cell gap (in the same px units as w/h) for a board of a
 * given size. Both the renderer and the tap fallback call this, so the drawn zones and
 * the tap hit-boxes are always the SAME rectangles.
 */
export function boardGeom(w: number, h: number): { pad: number; gap: number } {
  const m = Math.min(w, h);
  return { pad: Math.round(m * 0.03), gap: Math.round(m * 0.028) };
}

export function colOf(z: number, g: Grid): number {
  return z % g.cols;
}
export function rowOf(z: number, g: Grid): number {
  return Math.floor(z / g.cols);
}

/**
 * The rectangle for zone `z` inside a `w`×`h` area, with an outer `pad` and inter-cell
 * `gap`. Cells tile the padded area evenly.
 */
export function zoneRect(z: number, g: Grid, w: number, h: number, pad: number, gap: number): Rect {
  const innerW = w - pad * 2 - gap * (g.cols - 1);
  const innerH = h - pad * 2 - gap * (g.rows - 1);
  const cw = innerW / g.cols;
  const ch = innerH / g.rows;
  const col = colOf(z, g);
  const row = rowOf(z, g);
  return {
    x: pad + col * (cw + gap),
    y: pad + row * (ch + gap),
    w: cw,
    h: ch,
  };
}

/** Which zone contains point (px,py), or -1 if it lands in the gap/padding. */
export function zoneAt(px: number, py: number, g: Grid, w: number, h: number, pad: number, gap: number): number {
  for (let z = 0; z < zoneCount(g); z++) {
    const r = zoneRect(z, g, w, h, pad, gap);
    if (px >= r.x && px <= r.x + r.w && py >= r.y && py <= r.y + r.h) return z;
  }
  return -1;
}
